import { TherapyStatusBadge } from '@app/icare/components/status/TherapyStatusBadge'
import { PatientTherapyEditDrawer } from '@app/icare/entities/patient/edits/PatientTherapyEditDrawer'
import {
  patientFindingsQueryOptions,
  selectByGroupAndGroupByRelationId,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { patientQueryOptions } from '@app/icare/entities/patient/queries/patientQueryOptions'
import { patientTherapyQueries } from '@app/icare/entities/patient/queries/patientTherapyQueries'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import { TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import { RelationOverview } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationOverview'
import { useRelationCommitNavigation } from '@app/icare/routes/_app/patienten/$id/_edit/-components/useRelationCommitNavigation'
import { mapPatientTherapyToTherapyStatus } from '@app/icare/shared/api'
import { isActivePatientRelation } from '@app/icare/shared/api/form.type'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_therapy/therapien/')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(patientTherapyQueries.forIndex(params.id)),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: items = [] } = useSuspenseQuery(patientTherapyQueries.forIndex(id))
  const { data: patient } = useSuspenseQuery(patientQueryOptions(id))
  const { data: findings = {} } = useQuery({
    ...patientFindingsQueryOptions(id),
    select: selectByGroupAndGroupByRelationId('therapies'),
  })

  const handleAfterCommit = useRelationCommitNavigation<PatientTherapyRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/therapien/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })

  // Lingui macros must run inside a component so the active locale is bound at render-time.
  const labels = { label: t`Therapie`, labelPlural: t`Therapien` }

  return (
    <RelationOverview
      items={items}
      labels={labels}
      findings={findings}
      basePath={`/patienten/${id}/therapien`}
      getLabel={item => TherapyTypeEnum.$.getLabel(item.type)}
      onEdit={params =>
        void PatientTherapyEditDrawer.show({
          patientId: id,
          onAfterCommit: handleAfterCommit,
          ...params,
        })
      }
      hideSharedColumns={['period']}
      extraColumns={[
        {
          key: 'status',
          title: t`Status`,
          sorter: true,
          render: (_, record) => (
            <TherapyStatusBadge value={mapPatientTherapyToTherapyStatus(record, patient.deceased)} />
          ),
        },
      ]}
    />
  )
}
